// The full movement kit, learnable in one place — a left-edge panel players can open while
// in a room (operator, 2026-09-05: "put a full features panel on the left for learning").
// Pure UI: it reads nothing and sends nothing; it just names what the build can already do.
import { useState } from 'react';
import './ControlsPanel.css';

interface Row {
  keys: string;
  what: string;
}

interface Group {
  title: string;
  emoji: string;
  rows: Row[];
}

const GROUPS: Group[] = [
  {
    title: 'On foot',
    emoji: '🏃',
    rows: [
      { keys: 'WASD / arrows', what: 'run (camera-relative)' },
      { keys: 'Shift', what: 'sprint' },
      { keys: 'Alt', what: 'walk slowly' },
      { keys: 'A / D standing', what: 'turn on the spot' },
      { keys: 'Space', what: 'jump' },
      { keys: 'Click floor', what: 'walk there' },
    ],
  },
  {
    title: 'Get low',
    emoji: '🤸',
    rows: [
      { keys: 'C (hold)', what: 'crouch / crouch-walk' },
      { keys: 'Sprint + C', what: 'dive-slide' },
      { keys: 'Sprint at low gap', what: 'slides under it' },
    ],
  },
  {
    title: 'Climb',
    emoji: '🧗',
    rows: [
      { keys: 'Run at a wall', what: 'grab on — W/S up/down, A/D sideways' },
      { keys: 'Space on wall', what: 'let go' },
      { keys: 'Low fence', what: 'scrambles over by itself' },
    ],
  },
  {
    title: 'Fly',
    emoji: '🪂',
    rows: [
      { keys: 'Space in a fall', what: 'open the glider' },
      { keys: 'A / D', what: 'bank and turn' },
      { keys: 'W / S', what: 'dive for speed / stretch the glide' },
      { keys: 'Space again', what: 'stow it' },
      { keys: 'J (hold)', what: 'jetpack — fuel refills on the ground' },
    ],
  },
  {
    title: 'Social',
    emoji: '👋',
    rows: [
      { keys: '1–8', what: 'your gesture loadout' },
      { keys: 'Tap a player', what: 'open their profile' },
      { keys: 'Near a chair', what: 'sit / interact chips appear' },
    ],
  },
  {
    title: 'Camera',
    emoji: '🎥',
    rows: [
      { keys: 'Drag', what: 'look around (it settles back behind you)' },
      { keys: 'Scroll', what: 'zoom — the angle follows' },
    ],
  },
];

export function ControlsPanel() {
  const [open, setOpen] = useState(false);

  return (
    <div className={`controls-panel${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="controls-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? 'Hide controls' : 'Show controls'}
        title="Controls"
      >
        {open ? '✕' : '🎮'}
      </button>
      {open && (
        <div className="controls-body" role="dialog" aria-label="Controls">
          <div className="controls-head">Everything you can do</div>
          {GROUPS.map((g) => (
            <section key={g.title} className="controls-group">
              <h4>
                <span aria-hidden>{g.emoji}</span> {g.title}
              </h4>
              {g.rows.map((r) => (
                <div key={r.keys} className="controls-row">
                  <kbd>{r.keys}</kbd>
                  <span>{r.what}</span>
                </div>
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
